import { useMutation } from '@tanstack/react-query';
import moment, { Moment } from 'moment';
import { renewQuoteV2Mutation } from 'Services/Mutations/QuotesV2';
import { enqueueSnackbar } from 'notistack';
import type { PriceRefreshMethod } from 'Services/Query/QuotesV2/Types';
import { useQuoteCache } from './use-quote-cache';

interface RenewQuoteParams {
  validUntil: Date | Moment | string;
  refreshMethod?: PriceRefreshMethod;
}

/**
 * Hook to renew/extend a quote's expiration date
 * Refetches quote cache on success (NOT items — refetching items
 * would overwrite enriched data with lightweight metadata)
 */
export const useRenewQuote = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = renewQuoteV2Mutation();

  return useMutation({
    mutationFn: async ({
      validUntil,
      refreshMethod = 'KEEP_PRICING'
    }: RenewQuoteParams) => {
      let dateStr: string;
      if (typeof validUntil === 'string') {
        dateStr = validUntil;
      } else if (moment.isMoment(validUntil)) {
        dateStr = validUntil.startOf('day').format('YYYY-MM-DD');
      } else {
        dateStr = moment(validUntil).startOf('day').format('YYYY-MM-DD');
      }

      return mutationFn({
        quoteId,
        refreshMethod,
        validUntil: dateStr
      });
    },

    onSuccess: async () => {
      await cache.refetchQuote();

      enqueueSnackbar('Quote extended successfully', {
        variant: 'success'
      });
    },

    onError: () => {
      enqueueSnackbar('Failed to extend quote', {
        variant: 'error'
      });
    }
  });
};
